import React, { FormEvent, useEffect, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  Paper,
  Stack,
  TextField,
  ThemeProvider,
  Tooltip,
  Typography,
  createTheme,
} from '@mui/material';
import {
  ArrowBack,
  BadgeOutlined,
  ErrorOutline,
  Logout,
  VerifiedUser,
} from '@mui/icons-material';
import { btApi, TeacherIdCard, TeacherIdQr } from '../services/api/btApi';
import TeacherCardView from '../features/btcert/card/TeacherCard';

const teacherIdTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#166534', contrastText: '#ffffff' },
    secondary: { main: '#b45309' },
    background: { default: '#edf1ee', paper: '#ffffff' },
    text: { primary: '#14211d', secondary: '#52615b' },
  },
  typography: {
    fontFamily: '"Noto Sans KR", "Malgun Gothic", sans-serif',
    button: { textTransform: 'none', fontWeight: 700, letterSpacing: 0 },
    h4: { fontWeight: 800, letterSpacing: 0 },
    h5: { fontWeight: 800, letterSpacing: 0 },
  },
  shape: { borderRadius: 8 },
});

const pageBackground = {
  minHeight: '100dvh',
  py: { xs: 3, sm: 6 },
  backgroundColor: '#edf1ee',
  backgroundImage: 'linear-gradient(rgba(22, 101, 52, 0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(22, 101, 52, 0.045) 1px, transparent 1px)',
  backgroundSize: '24px 24px',
};

const idCardBackground = {
  minHeight: '100dvh',
  height: '100dvh',
  py: 0,
  overflow: 'hidden',
  backgroundColor: '#eef1ff',
};

const formatPhoneNumber = (value: string) => {
  const numbers = value.replace(/\D/g, '').slice(0, 11);
  if (numbers.length <= 3) return numbers;
  if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
  return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
};

const TeacherIdPage: React.FC = () => {
  const [card, setCard] = useState<TeacherIdCard | null>(null);
  const [qr, setQr] = useState<TeacherIdQr | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [churchLoading, setChurchLoading] = useState(false);
  const [churchOptions, setChurchOptions] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ churchName: '', teacherName: '', teacherPhone: '' });

  useEffect(() => {
    btApi.getMyTeacherId()
      .then(setCard)
      .catch(() => setCard(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!card) return;
    const loadQr = () => {
      btApi.getTeacherIdQr()
        .then(setQr)
        .catch((requestError) => setError(requestError.message || 'QR 코드를 불러오지 못했습니다.'));
    };
    loadQr();
    const intervalId = window.setInterval(loadQr, 60_000);
    return () => window.clearInterval(intervalId);
  }, [card]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    if (card) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = previousOverflow;
    }
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [card]);

  useEffect(() => {
    const keyword = form.churchName.trim();
    if (!keyword) {
      setChurchOptions([]);
      return;
    }

    const timer = window.setTimeout(async () => {
      try {
        setChurchLoading(true);
        const suggestions = await btApi.getTeacherIdChurchSuggestions(keyword);
        setChurchOptions(suggestions);
      } catch {
        setChurchOptions([]);
      } finally {
        setChurchLoading(false);
      }
    }, 250);

    return () => window.clearTimeout(timer);
  }, [form.churchName]);

  const handleLogin = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const teacherId = await btApi.loginTeacherId(form);
      setCard(teacherId);
    } catch (requestError: any) {
      setError(requestError.message || '증명 정보를 확인하지 못했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    await btApi.logoutTeacherId();
    setCard(null);
    setQr(null);
    setForm({ churchName: '', teacherName: '', teacherPhone: '' });
  };

  const verificationUrl = qr
    ? `${window.location.origin}/bt/teacher-id/verify?token=${encodeURIComponent(qr.token)}`
    : '';

  return (
    <ThemeProvider theme={teacherIdTheme}>
      <Box sx={card ? idCardBackground : pageBackground}>
        <Container maxWidth="sm">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: card ? 1.5 : 3, pt: card ? 1.5 : 0 }}>
            <Button component={RouterLink} to="/" startIcon={<ArrowBack />} sx={{ color: card ? '#1a2bf0' : '#14211d' }}>
              AWANA
            </Button>
            {card && (
              <Tooltip title="로그아웃">
                <IconButton onClick={handleLogout} aria-label="로그아웃" sx={{ width: 48, height: 48, color: '#1a2bf0' }}>
                  <Logout />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {loading ? (
            <Box sx={{ minHeight: 420, display: 'grid', placeItems: 'center' }}>
              <CircularProgress aria-label="증명 불러오는 중" />
            </Box>
          ) : !card ? (
            <Paper
              component="form"
              onSubmit={handleLogin}
              elevation={0}
              sx={{ maxWidth: 420, mx: 'auto', p: { xs: 3, sm: 4 }, border: '1px solid #cad5cf', borderRadius: '8px' }}
            >
              <Stack spacing={3}>
                <Box>
                  <BadgeOutlined sx={{ color: 'primary.main', fontSize: 36, mb: 1 }} />
                  <Typography component="h1" variant="h5">교사 모바일 증명</Typography>
                </Box>
                {error && <Alert severity="error">{error}</Alert>}
                <Autocomplete
                  freeSolo
                  options={churchOptions}
                  loading={churchLoading}
                  value={form.churchName}
                  onInputChange={(_, value) => setForm({ ...form, churchName: value })}
                  noOptionsText={form.churchName.trim() ? '검색 결과가 없습니다.' : '교회명을 입력해주세요.'}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="교회명"
                      placeholder="금광교회"
                      autoComplete="organization"
                      required
                      fullWidth
                    />
                  )}
                />
                <TextField
                  label="이름"
                  value={form.teacherName}
                  onChange={(event) => setForm({ ...form, teacherName: event.target.value })}
                  autoComplete="name"
                  required
                  fullWidth
                />
                <TextField
                  label="휴대전화"
                  value={form.teacherPhone}
                  onChange={(event) => setForm({ ...form, teacherPhone: formatPhoneNumber(event.target.value) })}
                  placeholder="010-0000-0000"
                  autoComplete="tel"
                  inputProps={{ inputMode: 'numeric', maxLength: 13 }}
                  required
                  fullWidth
                />
                <Button type="submit" variant="contained" size="large" disabled={submitting} sx={{ minHeight: 48 }}>
                  {submitting ? '확인 중' : '증명 확인'}
                </Button>
              </Stack>
            </Paper>
          ) : (
            <TeacherCardView
              base={{ keyCode: card.keyCode, teacherName: card.teacherName, churchName: card.churchName }}
              liveQrValue={verificationUrl}
            />
          )}
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export const TeacherIdVerifyPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [card, setCard] = useState<TeacherIdCard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setError('검증 정보가 없습니다.');
      setLoading(false);
      return;
    }
    btApi.verifyTeacherId(token)
      .then(setCard)
      .catch((requestError) => setError(requestError.message || '증명을 확인하지 못했습니다.'))
      .finally(() => setLoading(false));
  }, [searchParams]);

  return (
    <ThemeProvider theme={teacherIdTheme}>
      <Box sx={pageBackground}>
        <Container maxWidth="xs">
          <Paper elevation={0} sx={{ p: 3, border: '1px solid #cad5cf', borderRadius: '8px', textAlign: 'center' }}>
            {loading ? (
              <Box sx={{ minHeight: 300, display: 'grid', placeItems: 'center' }}>
                <CircularProgress aria-label="증명 검증 중" />
              </Box>
            ) : card ? (
              <Stack spacing={2.5} alignItems="center">
                <VerifiedUser color="success" sx={{ fontSize: 52 }} />
                <Box>
                  <Typography component="h1" variant="h5">유효한 증명</Typography>
                  <Typography color="text.secondary">실시간 확인 완료</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 26, fontWeight: 900 }}>{card.teacherName}</Typography>
                  <Typography sx={{ mt: 0.5, fontWeight: 700 }}>{card.churchName || '소속 교회 미등록'}</Typography>
                  <Typography color="text.secondary">{card.position}</Typography>
                </Box>
                <Typography sx={{ fontFamily: 'monospace', fontSize: 13 }}>{card.keyCode}</Typography>
              </Stack>
            ) : (
              <Stack spacing={2.5} alignItems="center" sx={{ py: 4 }}>
                <ErrorOutline color="error" sx={{ fontSize: 52 }} />
                <Typography component="h1" variant="h5">확인할 수 없는 증명</Typography>
                <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>
              </Stack>
            )}
          </Paper>
          <Button component={RouterLink} to="/bt/teacher-id" startIcon={<ArrowBack />} color="inherit" sx={{ mt: 2, minHeight: 48 }}>
            교사 증명
          </Button>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default TeacherIdPage;